import { useCallback, useState } from "react";
import { LanguageCode, SPANISH_ABBREV, ENGLISH_ABBREV } from "@bto/shared";
import {
  fetchReportDownloadUrl,
  ReportDownloadType,
} from "../reportDownloadRequest";
import { useAuthenticationHook } from "./useAuthenticationHook";
import { getReportFilename } from "../printUtils";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../analyticsEvents";

export { getAvailableLanguages } from "@bto/shared";

export type ReportType = ReportDownloadType;
export type DownloadLocation = "grid" | "report_screen";

interface UseReportDownloadReturn {
  downloadReport: (
    reportId: string,
    reportType: ReportType,
    downloadLocation: DownloadLocation,
    language?: LanguageCode,
    availableLanguages?: string[],
  ) => Promise<boolean>;
  isDownloading: boolean;
  isDownloadingSpanish: boolean;
}

/**
 * Hook that provides functions to download reports with optional language support.
 * Handles authentication, API calls, and file download.
 */
export const useReportDownload = (): UseReportDownloadReturn => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingSpanish, setIsDownloadingSpanish] = useState(false);

  const downloadReport = useCallback(
    async (
      reportId: string,
      reportType: ReportType,
      downloadLocation: DownloadLocation,
      language?: LanguageCode,
      availableLanguages?: string[],
    ): Promise<boolean> => {
      if (!auth?.token || !reportId) return false;

      const effectiveLanguage: LanguageCode = language ?? ENGLISH_ABBREV;
      const isSpanish = effectiveLanguage === SPANISH_ABBREV;
      if (isSpanish) {
        setIsDownloadingSpanish(true);
      } else {
        setIsDownloading(true);
      }

      let downloadSucceeded = false;

      try {
        const response = await fetchReportDownloadUrl(
          auth.token,
          reportType,
          reportId,
          setLoggedOut,
          downloadLocation,
          language,
        );

        if (response.ok) {
          const pdf = await response.json();
          if (pdf.pdf_url) {
            // Fire (and await) the analytics event BEFORE triggering the
            // download so the request both starts and completes before
            // `anchor.click()` can cause any navigation that would abort it.
            //
            // Race the await against a 2s timeout so a slow/hung analytics
            // backend can never block the actual download. The underlying
            // fetch keeps running in the background after the race resolves,
            // so the event may still land on the server — we just stop
            // waiting for it. Analytics is non-critical; the download isn't.
            await Promise.race([
              TrackAnalyticsEvent(
                AnalyticsEventNames.MultiLanguageReportDownloaded,
                auth.token,
                auth.username,
                {
                  report_type: reportType,
                  language: effectiveLanguage,
                  available_languages: (
                    availableLanguages ?? [ENGLISH_ABBREV]
                  ).join(","),
                  location: downloadLocation,
                  portal_type: "provider",
                },
              ).catch(() => undefined),
              new Promise((resolve) => setTimeout(resolve, 2000)),
            ]);

            const anchor = document.createElement("a");
            anchor.href = pdf.pdf_url;
            anchor.download = getReportFilename(pdf.pdf_url, effectiveLanguage);
            anchor.click();
            anchor.remove();

            downloadSucceeded = true;
          }
        }
      } catch {
        console.error("Error downloading report for reportId: ", reportId);
      } finally {
        if (isSpanish) {
          setIsDownloadingSpanish(false);
        } else {
          setIsDownloading(false);
        }
      }

      return downloadSucceeded;
    },
    [auth?.token, setLoggedOut],
  );

  return { downloadReport, isDownloading, isDownloadingSpanish };
};
